import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import SearchInput from "../../../../core/Inputs/SearchInput";
import Svg from "../../../../component/common/Svg";
import { useDispatch } from "react-redux";
import { getTokenData } from "../../../../store/actions/authAction";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import moment from "moment";
import SurveyGroupModal from "../../../../component/common/modals/SurveyGroupModal";
import RenameGroupModal from "../../../../component/common/modals/RenameGroupModal";
import { getSurveyList } from "../../../../store/actions/surveyAction";
import {
  getSurveyGroups,
  createSurveyGroup,
  updateSurveyGroup,
  deleteSurveyGroup,
} from "../../../../store/actions/surveyGroupAction";
import { toast } from "react-toastify";
import Card from "../../../../core/Card";
import style from "../../../style.module.css";

export default function Index() {
  //States
  const initialFilters = { search: "", status: "" };
  const { t } = useTranslation();

  const [userId, setuserId] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [sortField, setSortField] = useState("added_on");
  const [sortDirection, setSortDirection] = useState("desc");
  const dispatch = useDispatch();
  const { surveyData: surveyList } = useSelector((state) => state.surveyData);
  const { groupList, groupListLoading } = useSelector(
    (state) => state.surveyGroupData
  );

  // Helper: fetch groups from backend using action
  const fetchGroups = () => {
    dispatch(getSurveyGroups());
  };

  //Effects
  useEffect(() => {
    dispatch(getTokenData(setuserId));
  }, [userId, dispatch]);

  // Initial groups fetch
  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch surveys for the modal
  useEffect(() => {
    if (isGroupModalOpen && userId) {
      const payload = {
        user_id: userId,
        perPage: 1000, // Get all surveys for selection
        page: 1,
        sort: "DESC",
        sortby: "su_survey_modified_date_time",
        search: "",
        status: "",
        su_languages: "",
      };
      dispatch(getSurveyList(payload));
    }
  }, [isGroupModalOpen, userId, dispatch]);

  //Functionalities
  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
  };

  // Handle sorting
  const handleSort = (column, sortDirectionParam) => {
    const columnToField = {
      "Group Name": "name",
      "Total Surveys": "surveyCount",
      "Added on / Added by": "added_on",
      "Published on": "published_on",
    };

    const field = columnToField[column.name] || "added_on";
    setSortField(field);
    setSortDirection(sortDirectionParam.toLowerCase());
  };

  // Filter and sort groups
  const filteredAndSortedGroups = useMemo(() => {
    if (!groupList) return [];

    // Filter by search
    let filtered = groupList.filter((group) => {
      const matchesSearch =
        !filters.search ||
        group.name.toLowerCase().includes(filters.search.toLowerCase());
      return matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Convert to lowercase for string comparison
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      // Compare values
      if (aValue < bValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [groupList, filters.search, sortField, sortDirection]);

  // Handle row selection
  const handleRowSelected = (state) => {
    setSelectedRows(state.selectedRows);
  };

  // Conditional row styles to add class to selected rows
  const conditionalRowStyles = [
    {
      when: (row) => {
        return selectedRows.some((selectedRow) => selectedRow.id === row.id);
      },
      classNames: ["row-selected"],
    },
  ];

  //Column definitions
  const columns = [
    {
      name: t("survey.groups.groupName"),
      sortable: true,
      // width: "250px",
      cell: (row) => {
        return (
          <div className="w-100">
            <div className="d-flex align-items-center">
              <div className="me-2 icon-circle">
                <Svg name="folder-icon-big" />
              </div>
              <div className="flex-grow-1">
                <Link
                  to={`/admin/survey/groups/${row.id}`}
                  className="surv-title text-decoration-none"
                  style={{ color: "inherit" }}
                >
                  {row.name}
                </Link>
              </div>
            </div>
          </div>
        );
      },
    },

    {
      name: t("survey.groups.totalSurveys"),
      selector: (row) => row.surveyCount,
      sortable: true,
      // width: "120px",
      cell: (row) => {
        return <div className="w-100">{row.surveyCount ?? 0}</div>;
      },
    },
    {
      name: t("survey.addedOnAddedBy"),
      selector: (row) => row.added_on,
      sortable: true,
      cell: (row) => {
        let formattedAddedOn = "";
        if (row.added_on) {
          // Backend returns MySQL datetime format: YYYY-MM-DD HH:mm:ss
          formattedAddedOn = moment(row.added_on, "YYYY-MM-DD HH:mm:ss").format(
            "DD-MM-YYYY"
          );
        }
        return (
          <div className="w-100">
            {formattedAddedOn || "-"}
            {row.added_by && (
              <div className="secondary-text bold">{row.added_by}</div>
            )}
          </div>
        );
      },
    },
    // {
    //   name: t("survey.publishedOn"),
    //   selector: (row) => row.published_on,
    //   sortable: true,
    //   cell: (row) => {
    //     let formattedPublishedOn = "";
    //     if (row.published_on) {
    //       // Backend returns MySQL datetime format: YYYY-MM-DD HH:mm:ss
    //       formattedPublishedOn = moment(row.published_on, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY");
    //     }
    //     return <div className="w-100">{formattedPublishedOn || "-"}</div>;
    //   },
    // },
    {
      name: t("survey.customVariable.action"),
      width: "85px",
      cell: (row) => (
        <div className="dropdown table-dropdown">
          <button
            className="btn dropdown-tgl"
            type="button"
            id="viewoptions"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <Svg name="action" />
          </button>
          <div className="dropdown-menu" aria-labelledby="viewoptions">
            <Link
              to={`/admin/survey/groups/${row.id}`}
              className="dropdown-item d-flex align-items-center text-decoration-none"
              style={{ color: "inherit" }}
            >
              <div className="icon-circle me-3">
                <Svg name="Edit-Survey" />
              </div>
              <span>{t("survey.groups.actions.manage")}</span>
            </Link>
            <Link
              to={`/admin/survey/groups/${row.id}/share`}
              className="dropdown-item d-flex align-items-center text-decoration-none"
              style={{ color: "inherit" }}
            >
              <div className="icon-circle me-3">
                <Svg name="share" />
              </div>
              <span>{t("survey.share.tittle") || "Publish & Share"}</span>
            </Link>
            <span
              className="dropdown-item d-flex align-items-center"
              onClick={() => {
                setRenamingGroup(row);
                setIsRenameModalOpen(true);
              }}
            >
              <div className="icon-circle me-3">
                <Svg name="Group-rename" />
              </div>
              <span>{t("survey.groups.rename")}</span>
            </span>
            <span
              className="dropdown-item d-flex align-items-center"
              onClick={() => {
                setDeletingGroup(row);
                setIsDeleteModalOpen(true);
              }}
            >
              <div className="icon-circle me-3">
                <Svg name="delete" />
              </div>
              <span>{t("settings.roles.delete_action.delete")}</span>
            </span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex-grow-1 dashboard-page-outer">
        <div className="dashboard-page-wrap">
          <div className="page-heading d-md-flex align-items-center justify-content-between mb-20">
            <div className="head-lef">
              <div className="survey-tabs-nav">
                <Link to="/admin/survey/list" className="survey-tab-link">
                  {t("survey.heading")}
                </Link>
                <Link
                  to="/admin/survey/groups"
                  className="survey-tab-link active"
                >
                  {t("survey.groups.title")}
                </Link>
              </div>
            </div>
            <div className="right-wrap mt-2 mt-md-0 d-flex align-items-center gap-2">
              {/* Delete button - only show when rows are selected */}
              {/* {selectedRows.length > 0 && (
                                <button
                                    className="btn btn-bulk-delete"
                                    type="button"
                                    title="Delete"
                                >
                                    <Svg name="trash-red" />
                                    Delete
                                </button>
                            )} */}

              {/* Create new button with dropdown */}
              {window.modulePermissions?.M1?.add === 1 && (
                <div className="dropdown">
                  <button
                    className="btn btn-primary add-btn"
                    type="button"
                    id="createNewDropdown"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <Svg name="plus" />
                    Create new
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={20}
                      height={20}
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        className="fill-none"
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div
                    className="dropdown-menu"
                    aria-labelledby="createNewDropdown"
                  >
                    <Link
                      to="/admin/survey/create/start"
                      className="dropdown-item"
                    >
                      {t("survey.heading")}
                    </Link>
                    <button
                      type="button"
                      className="dropdown-item"
                      style={{ paddingLeft: "10px", fontSize: "15px" }}
                      onClick={() => {
                        setEditingGroup(null);
                        setIsGroupModalOpen(true);
                      }}
                    >
                      {t("survey.groups.surveyGroup")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="table-wrapper-view">
            <div className="search-filter d-md-flex align-items-center surveyfilter">
              <div className="flex-grow-1 search-filter-padding">
                <SearchInput
                  className="custom-search-input"
                  type="def"
                  view="outlined"
                  color="primary"
                  placeholder={t('settings.users_and_teams.users.search')}
                  fill={true}
                  onSearch={handleSearch}
                  onClear={() => setFilters({ ...filters, search: "" })}
                />
              </div>
              {/* <div className="textS d-flex align-items-center">
                                <label className="text-label"> {t('survey.status')}:</label>
                                <div className="dropdown table-dropdown ques-options">
                                    <button className="btn dropdown-tgl" type="button" id="quesoptions" data-bs-toggle="dropdown"
                                        aria-haspopup="true" aria-expanded="false">
                                        {status ? status.name : t('survey.allL')}
                                    </button>

                                    <div className="dropdown-menu" aria-labelledby="quesoptions">
                                        <span className="dropdown-item" onClick={() => handleClickDropDown(t('survey.allL'))}>{t('survey.allL')} </span>
                                        {surveyStatus?.map((opt, index) => {
                                            return (
                                                <span
                                                    className="dropdown-item"
                                                    key={index}
                                                    onClick={() => handleClickDropDown(opt)}
                                                > {opt.name} </span>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div> */}
            </div>
            <div className="tablePage bg-white">
              <div className="content-table survey-table">
                <div className="data-table-wrap custom-data-table">
                  <DataTable
                    title=""
                    columns={columns}
                    data={filteredAndSortedGroups}
                    defaultSortFieldId={3}
                    onSort={handleSort}
                    pagination
                    paginationServer={false}
                    progressPending={groupListLoading}
                    //selectableRows
                    selectableRowsHighlight
                    onSelectedRowsChange={handleRowSelected}
                    conditionalRowStyles={conditionalRowStyles}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Survey Group Modal */}
      <SurveyGroupModal
        open={isGroupModalOpen}
        onSuccess={(modalData) => {
          // Build survey payload items from selected ids using current survey list
          const buildSurveyItems = (ids) => {
            if (!Array.isArray(ids) || !ids.length) return [];
            const languageSet = new Set();
            const items = [];

            ids.forEach((id, index) => {
              const survey = (surveyList || []).find((s) => s.survey_id === id);
              if (!survey) return;

              const langCode = (survey.su_languages || "").toString().trim();
              if (!langCode || languageSet.has(langCode)) {
                return;
              }

              languageSet.add(langCode);
              items.push({
                su_survey_id: id,
                su_language_code: langCode,
                su_is_primary: index === 0 ? 1 : 0,
                su_sort_order: index + 1,
                su_is_active: 1,
              });
            });

            return items;
          };

          // Create group
          if (modalData.mode === "create") {
            const surveyItems = buildSurveyItems(modalData.surveyIds);
            if (!surveyItems.length) {
              toast.error(t("survey.groups.toast.selectAtLeastOne"));
              return;
            }

            const primaryItem =
              surveyItems.find((i) => i.su_is_primary === 1) || surveyItems[0];

            dispatch(
              createSurveyGroup(
                {
                  group_title: modalData.name,
                  default_survey_id: primaryItem?.su_survey_id,
                  surveys: surveyItems,
                },
                () => {
                  fetchGroups();
                  toast.success(t("survey.groups.toast.created"));
                  setIsGroupModalOpen(false);
                  setEditingGroup(null);
                },
                (error) => {
                  const backendMsg =
                    error.response?.data?.msg ||
                    t("survey.groups.toast.createError");
                  toast.error(backendMsg);
                }
              )
            );
          }
        }}
        onCancel={() => {
          setIsGroupModalOpen(false);
          setEditingGroup(null);
        }}
        groupData={editingGroup}
        surveysList={surveyList || []}
        selectedSurveyIds={editingGroup?.surveyIds || []}
      />

      {/* Rename Group Modal */}
      <RenameGroupModal
        open={isRenameModalOpen}
        onSuccess={(data) => {
          dispatch(
            updateSurveyGroup(
              {
                group_id: data.id,
                group_title: data.name,
              },
              () => {
                fetchGroups();
                toast.success(t("survey.groups.toast.renamed"));
                setIsRenameModalOpen(false);
                setRenamingGroup(null);
              },
              (error) => {
                const backendMsg =
                  error.response?.data?.msg || t("survey.groups.toast.renameError");
                toast.error(backendMsg);
              }
            )
          );
        }}
        onCancel={() => {
          setIsRenameModalOpen(false);
          setRenamingGroup(null);
        }}
        groupData={renamingGroup}
      />

      {/* Delete Group Modal */}
      <div
        className={`customvar-modal modal custom-modal fade deleteModal ${
          isDeleteModalOpen ? "show" : ""
        }`}
        style={{ display: `${isDeleteModalOpen ? "block" : "none"}` }}
        id="deleteGroupModal"
        tabIndex={-1}
        aria-labelledby="deleteGroupModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className={"formcontainer " + style.container}>
                <div
                  className={
                    "formvawrapper delete-modal-wrap modal-setting-medium " +
                    style.wrapper
                  }
                >
                  <Card view="raised" className={"formstyle p-0 " + style.card}>
                    <div className="delete-body-wrap delete-language">
                      <div className="heading">
                        <h3>{t("survey.groups.deleteGroup")}</h3>
                        <p>
                          {t("survey.groups.areYouSure")}
                        </p>
                      </div>
                      <div className="warning-message">
                        <div className="title d-flex align-items-center">
                          <svg
                            width={40}
                            height={42}
                            viewBox="0 0 40 42"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 2L37.3205 32H2.67949L20 2Z"
                              stroke="#F05454"
                              strokeWidth={3}
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20 13.5385V22"
                              stroke="#F05454"
                              strokeWidth={3}
                            />
                            <circle
                              cx="19.9994"
                              cy="25.8461"
                              r={1}
                              fill="black"
                              stroke="#F05454"
                              strokeWidth="1.07692"
                            />
                          </svg>
                          <h4>{t("survey.groups.deleteWarning")}</h4>
                        </div>
                        <p>
                          {t("survey.groups.deleteMessage")} <strong>{deletingGroup?.name}</strong> {t("survey.groups.deleteConfirmation")}
                        </p>
                      </div>
                      <div className="button-wrap d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-bordered color-delete"
                          onClick={() => {
                            setIsDeleteModalOpen(false);
                            setDeletingGroup(null);
                          }}
                        >
                          {t('survey.customVariable.cancel')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-delete ms-3"
                          onClick={() => {
                            if (deletingGroup?.id) {
                              dispatch(
                                deleteSurveyGroup(
                                  {
                                    group_id: deletingGroup.id,
                                  },
                                  () => {
                                    fetchGroups();
                                    toast.success(
                                      t("survey.groups.toast.deleted")
                                    );
                                    setIsDeleteModalOpen(false);
                                    setDeletingGroup(null);
                                  },
                                  (error) => {
                                    const backendMsg =
                                      error.response?.data?.msg ||
                                      t("survey.groups.toast.deleteError");
                                    toast.error(backendMsg);
                                    setIsDeleteModalOpen(false);
                                    setDeletingGroup(null);
                                  }
                                )
                              );
                            }
                          }}
                        >
                         {t("settings.roles.delete_action.delete")}
                        </button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Backdrop */}
      <div
        className={`modal-backdrop fade ${isDeleteModalOpen ? "show" : ""}`}
        style={{ display: `${isDeleteModalOpen ? "block" : "none"}` }}
        onClick={() => {
          setIsDeleteModalOpen(false);
          setDeletingGroup(null);
        }}
      ></div>
    </>
  );
}
